/**
 * Cart Context
 *
 * Provides global shopping cart state using React Context.
 * Wraps cartService to make cart accessible throughout the app.
 */

import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import cartService from '../services/cartService';
import { useAuth } from './AuthContext';

const CartContext = createContext(null);

export const CartProvider = ({ children }) => {
  const { isSubscriber } = useAuth();

  // Cart state
  const [cart, setCart] = useState({
    items: cartService.getItems(),
    itemCount: cartService.getItemCount(),
    uniqueItemCount: cartService.getUniqueItemCount(),
    subtotal: cartService.getSubtotal(),
    shipping: cartService.getEstimatedShipping(),
    total: cartService.getTotal(),
    isEmpty: cartService.getItems().length === 0,
  });

  // Update cart state when cartService changes
  useEffect(() => {
    const unsubscribe = cartService.addListener((cartData) => {
      setCart({
        ...cartData,
        isEmpty: cartData.items.length === 0,
      });
    });

    return unsubscribe;
  }, []);

  /**
   * Add item to cart
   */
  const addItem = useCallback((item) => {
    const result = cartService.addItem(item);

    if (result.success) {
      // Update state immediately
      setCart({
        items: cartService.getItems(),
        itemCount: cartService.getItemCount(),
        uniqueItemCount: cartService.getUniqueItemCount(),
        subtotal: cartService.getSubtotal(),
        shipping: cartService.getEstimatedShipping(),
        total: cartService.getTotal(),
        isEmpty: false,
      });
    }

    return result;
  }, []);

  /**
   * Remove item from cart
   */
  const removeItem = useCallback((listingId, size, color = null) => {
    const result = cartService.removeItem(listingId, size, color);

    if (result.success) {
      setCart({
        items: cartService.getItems(),
        itemCount: cartService.getItemCount(),
        uniqueItemCount: cartService.getUniqueItemCount(),
        subtotal: cartService.getSubtotal(),
        shipping: cartService.getEstimatedShipping(),
        total: cartService.getTotal(),
        isEmpty: cartService.getItems().length === 0,
      });
    }

    return result;
  }, []);

  /**
   * Update item quantity
   */
  const updateQuantity = useCallback((listingId, size, color = null, quantity) => {
    const result = cartService.updateQuantity(listingId, size, color, quantity);

    if (result.success) {
      setCart({
        items: cartService.getItems(),
        itemCount: cartService.getItemCount(),
        uniqueItemCount: cartService.getUniqueItemCount(),
        subtotal: cartService.getSubtotal(),
        shipping: cartService.getEstimatedShipping(),
        total: cartService.getTotal(),
        isEmpty: cartService.getItems().length === 0,
      });
    }

    return result;
  }, []);

  /**
   * Clear entire cart
   */
  const clearCart = useCallback(() => {
    const result = cartService.clearCart();

    if (result.success) {
      setCart({
        items: [],
        itemCount: 0,
        uniqueItemCount: 0,
        subtotal: 0,
        shipping: 0,
        total: 0,
        isEmpty: true,
      });
    }

    return result;
  }, []);

  /**
   * Check if item is in cart
   */
  const isInCart = useCallback((listingId, size, color = null) => {
    return cartService.isInCart(listingId, size, color);
  }, []);

  /**
   * Get item quantity
   */
  const getItemQuantity = useCallback((listingId, size, color = null) => {
    return cartService.getItemQuantity(listingId, size, color);
  }, []);

  /**
   * Get specific item from cart
   */
  const getItem = useCallback((listingId, size, color = null) => {
    return cartService.getItem(listingId, size, color);
  }, []);

  /**
   * Validate cart items
   */
  const validateCart = useCallback(() => {
    return cartService.validateCart(isSubscriber);
  }, [isSubscriber]);

  /**
   * Export cart for checkout
   */
  const exportForCheckout = useCallback(() => {
    return cartService.exportForCheckout();
  }, []);

  /**
   * Get cart summary
   */
  const getSummary = useCallback(() => {
    return cartService.getSummary();
  }, []);

  /**
   * Refresh cart state (useful after authentication changes)
   */
  const refreshCart = useCallback(() => {
    setCart({
      items: cartService.getItems(),
      itemCount: cartService.getItemCount(),
      uniqueItemCount: cartService.getUniqueItemCount(),
      subtotal: cartService.getSubtotal(),
      shipping: cartService.getEstimatedShipping(),
      total: cartService.getTotal(),
      isEmpty: cartService.getItems().length === 0,
    });
  }, []);

  const value = {
    // Cart state
    items: cart.items,
    itemCount: cart.itemCount,
    uniqueItemCount: cart.uniqueItemCount,
    subtotal: cart.subtotal,
    shipping: cart.shipping,
    total: cart.total,
    isEmpty: cart.isEmpty,

    // Cart operations
    addItem,
    removeItem,
    updateQuantity,
    clearCart,

    // Cart queries
    isInCart,
    getItemQuantity,
    getItem,
    validateCart,
    exportForCheckout,
    getSummary,
    refreshCart,
  };

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
};

/**
 * Custom hook to use cart context
 */
export const useCart = () => {
  const context = useContext(CartContext);

  if (!context) {
    throw new Error('useCart must be used within a CartProvider');
  }

  return context;
};

export default CartContext;
